import * as _4 from 'lodash4';
import { AccountStatusDatastore } from '../datastore/account_status_datastore';
import { AccountIdentityDatastore } from '../datastore/account_identity_datastore';

const AccountModelInterface = {
  id: 'integer',
  username: 'string',
  email: 'email',
  token: 'string',
  tracking_id: 'string',
  gainsight_id: 'string',
  account_type: 'string',
  account_name: 'string',
  fullname: 'string',
  first_login: {
    type: 'boolean',
    writeToKey: 'isFirstLogin'
  },
  first_name: {
    type: 'string',
    writeToKey: 'firstName'
  },
  last_name: {
    type: 'string',
    writeToKey: 'lastName'
  },
  student_nk: 'string',
  student_id: 'integer',
  channel_name: 'string',
  is_advisor: {
    type: 'boolean',
    writeToKey: 'isAdvisor'
  },
  is_instructor: {
    type: 'boolean',
    writeToKey: 'isInstructor'
  },
  is_superuser: {
    type: 'boolean',
    writeToKey: 'isSuperuser'
  },
  isContentManager: 'boolean',
  group_names: {
    type: 'array',
    writeToKey: 'groupNames'
  },
  partner_nk: 'string',
  permissions: {
    type: 'array'
  },
  tandc_accepted: {
    optional: true,
    type: 'tri-state',
    writeToKey: 'acceptedTerms'
  },
  tandc_text: {
    optional: true,
    type: 'html',
    writeToKey: 'termsHTML',
    defaultValue: undefined
  },
  intro_accepted: {
    optional: true,
    type: 'tri-state',
    writeToKey: 'introAccepted'
  },
  year_classification: {
    optional: true,
    type: 'integer',
    writeToKey: 'yearClassification'
  },
  is_impersonating: {
    optional: true,
    type: 'boolean',
    writeToKey: 'isImpersonating'
  }
};

export class AccountModel {
  public id: number;
  public username: string;
  public email: string;
  public token: string;
  public tracking_id: string;
  public gainsight_id: string;
  public account_type: string;
  public account_name: string;

  public fullname: string;
  public isFirstLogin: boolean;
  public firstName: string;
  public lastName: string;
  public student_nk: string;
  public student_id: number;
  public channel_name: string;
  public isAdvisor: boolean;
  public isInstructor: boolean;
  public isSuperuser: boolean;
  public isContentManager: boolean;
  public isStudent: boolean;
  public groupNames: string[];
  public permissions: string[];
  public partner_nk: string;
  public acceptedTerms: boolean;
  public termsHTML: string;
  public introAccepted: boolean;
  public yearClassification: number;

  private permissionStrings: {
    student: string;
    advisor: string;
    instructor: string;
    contentManager: string;
  };

  constructor(
    private Interface: any,
    private ngAccountStatusDatastore: AccountStatusDatastore,
    private ngAccountIdentityDatastore: AccountIdentityDatastore
  ) {
    this.permissionStrings = {
      student: 'core.use_student',
      advisor: 'core.use_advisor',
      instructor: '',
      contentManager: 'cms.use_contentmanager'
    };
    this.initialize();
  }

  clear() {
    this.ngAccountIdentityDatastore.initialize();
    this.ngAccountStatusDatastore.initialize();
    return this.synchronize();
  }

  initialize() {
    return this.clear();
  }

  update(data: any) {
    this.ngAccountIdentityDatastore.update(data);
    this.ngAccountStatusDatastore.update(data);
    return this.synchronize();
  }

  synchronize() {
    const o = _4.merge(
      {},
      this.ngAccountIdentityDatastore.read((store) => store),
      this.ngAccountStatusDatastore.read((store) => store)
    );
    this.Interface.implement(AccountModelInterface, o, this);
    _4.merge(this, this.__getUserTypeInfo());
    return this;
  }

  export() {
    return this.Interface.serialize(AccountModelInterface, this);
  }

  isLoggedIn() {
    return !_4.isEmpty(this.username);
  }

  isValid() {
    return this.isLoggedIn();
  }

  private __getUserTypeInfo() {
    return {
      isAdvisor: this.__isAdvisor(),
      isInstructor: this.__isInstructor(),
      isContentManager: this.__isContentManager(),
      isStudent: this.__isStudent()
    };
  }

  private __isStudent() {
    return !_4.isEmpty(this.permissions) && _4.includes(this.permissions, this.permissionStrings.student);
  }

  private __isInstructor() {
    return !_4.isEmpty(this.groupNames) && _4.includes(this.groupNames, 'instructor');
  }

  private __isContentManager() {
    return !_4.isEmpty(this.permissions) && _4.includes(this.permissions, this.permissionStrings.contentManager);
  }

  private __isAdvisor() {
    return !_4.isEmpty(this.groupNames) && _4.includes(this.groupNames, 'advisor');
  }
}



// WEBPACK FOOTER //
// ./app/vendor/data-ng-authentication/src/models/account_model.ts